import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TrstdAppLoginIcon } from 'components/Illustrations/TrstdAppLoginIcon/TrstdAppLoginIcon';
import {
  Banner,
  CloseButton,
  Cta,
  Headline,
  IconWrap,
  Subline,
  TextCol
} from './MobileAppDownloadBanner.styles';
import {
  MOBILE_APP_DOWNLOAD_BANNER,
  MOBILE_APP_DOWNLOAD_BANNER_DISMISS
} from 'services/data-test-attributes';

const STORAGE_KEY = 'trstd_profile_mobile_app_banner_dismissed';
const MOBILE_MAX_PX = 575;
const BANNER_SPACE_VAR = '--mobile-app-banner-space';
const BANNER_SPACE_VALUE = '88px';

function getDownloadHref(): string {
  const url = process.env.NEXT_PUBLIC_TRSTD_CONSUMER_APP_DOWNLOAD_URL?.trim();
  return url && url.length > 0
    ? url
    : 'https://my.trstd.com/redirect.html?utm_source=search&utm_medium=web&utm_campaign=company-search-banner';
}

const MobileAppDownloadBanner = ({ embedActive }: { embedActive: boolean }) => {
  const { formatMessage } = useIntl();
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);

  const shouldShowForViewport = useCallback(() => {
    /* istanbul ignore next: useEffect only calls this in the browser */
    if (typeof window === 'undefined') {
      return false;
    }
    if (embedActive) {
      return false;
    }
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') {
        return false;
      }
    } catch {
      /* private mode / disabled storage */
    }
    return window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
  }, [embedActive]);

  useEffect(() => {
    if (embedActive) {
      setVisible(shouldShowForViewport());
      setReady(true);
      return;
    }

    const evaluate = () => {
      setVisible(shouldShowForViewport());
      setReady(true);
    };

    evaluate();
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`);
    mq.addEventListener('change', evaluate);
    return () => mq.removeEventListener('change', evaluate);
  }, [embedActive, shouldShowForViewport]);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty(BANNER_SPACE_VAR);
      return;
    }
    document.documentElement.style.setProperty(
      BANNER_SPACE_VAR,
      BANNER_SPACE_VALUE
    );
    return () => {
      document.documentElement.style.removeProperty(BANNER_SPACE_VAR);
    };
  }, [visible]);

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!ready || !visible) {
    return null;
  }

  const href = getDownloadHref();

  return (
    <Banner data-test={MOBILE_APP_DOWNLOAD_BANNER}>
      <CloseButton
        type="button"
        aria-label={formatMessage({
          id: 'MOBILE_APP_BANNER.CLOSE_ARIA_LABEL'
        })}
        onClick={handleDismiss}
        data-test={MOBILE_APP_DOWNLOAD_BANNER_DISMISS}
      >
        ×
      </CloseButton>
      <IconWrap aria-hidden>
        <TrstdAppLoginIcon />
      </IconWrap>
      <TextCol>
        <Headline>
          {formatMessage({ id: 'MOBILE_APP_BANNER.HEADLINE' })}
        </Headline>
        <Subline>{formatMessage({ id: 'MOBILE_APP_BANNER.SUBLINE' })}</Subline>
      </TextCol>
      <Cta href={href} target="_blank" rel="noopener noreferrer">
        {formatMessage({ id: 'MOBILE_APP_BANNER.GET_CTA' })}
      </Cta>
    </Banner>
  );
};

export default MobileAppDownloadBanner;
